import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ResidentFormData } from "../../schemas/residentForm";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

interface ResidentTypeSwitcherProps {
  displayIndex: number;
  disabled?: boolean;
}

// Helper to check if tenant has any entered data
const hasTenantData = (tenant: unknown): boolean => {
  if (!tenant || typeof tenant !== "object") return false;

  // Check common fields that might have data
  const fieldsToCheck = [
    "nom",
    "prenom",
    "email",
    "telephone",
    "rue",
    "numero",
    "npa",
    "ville",
    "nom_societe",
    "nom_representant",
    "prenom_representant",
    "email_representant",
    "date_naissance",
    "nationalite",
    "tel_professionnel",
    "tel_portable",
  ];

  const t = tenant as Record<string, unknown>;

  return fieldsToCheck.some((field) => {
    const value = t[field];
    return value && String(value).trim() !== "";
  });
};

export const ResidentTypeSwitcher: React.FC<ResidentTypeSwitcherProps> = ({
  displayIndex,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext<ResidentFormData>();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingType, setPendingType] = useState<"particulier" | "entreprise" | null>(null);

  const individualTenants = (useWatch<ResidentFormData>({ name: "individual_tenants" }) ||
    []) as ResidentFormData["individual_tenants"];
  const enterpriseTenants = (useWatch<ResidentFormData>({ name: "enterprise_tenants" }) ||
    []) as ResidentFormData["enterprise_tenants"];

  // Get all tenants sorted by tenant_index (same logic as in ResidentForm)
  const getAllTenantsSorted = () => {
    const individual = individualTenants.map((t, idx) => ({
      ...t,
      tenant_index: t.tenant_index ?? idx,
      _type: "particulier" as const,
      _arrayIndex: idx,
    }));
    const enterprise = enterpriseTenants.map((t, idx) => ({
      ...t,
      tenant_index: t.tenant_index ?? individual.length + idx,
      _type: "entreprise" as const,
      _arrayIndex: idx,
    }));
    return [...individual, ...enterprise].sort(
      (a, b) => (a.tenant_index ?? 0) - (b.tenant_index ?? 0)
    );
  };

  const allTenantsSorted = getAllTenantsSorted();
  const tenant = allTenantsSorted[displayIndex];
  const currentStatus = tenant?.status || "particulier";
  const tenantIndex = tenant?.tenant_index;
  const arrayIndex = tenant?._arrayIndex ?? 0;

  const handleTypeChange = (newType: "particulier" | "entreprise") => {
    if (disabled || currentStatus === newType) return;

    if (!tenant) return;

    // Check if tenant has data and show confirmation dialog
    if (hasTenantData(tenant)) {
      setPendingType(newType);
      setShowConfirmDialog(true);
    } else {
      performTypeChange(newType);
    }
  };

  const performTypeChange = (newType: "particulier" | "entreprise") => {
    if (!tenant) return;

    // Preserve tenant_index and is_guarantor when switching
    const preservedTenantIndex = tenant.tenant_index ?? tenantIndex;
    // First tenant (lowest tenant_index) should never be a guarantor
    const isFirstTenant =
      allTenantsSorted.length > 0 && allTenantsSorted[0].tenant_index === preservedTenantIndex;
    const preservedIsGuarantor = isFirstTenant ? false : tenant.is_guarantor === true; // First tenant is never guarantor

    if (newType === "particulier") {
      const newTenant = {
        status: "particulier" as const,
        civilite: undefined as unknown as "monsieur" | "madame" | "ne_souhaite_pas_communiquer",
        nom: "",
        prenom: "",
        date_naissance: "",
        nationalite: "",
        email: "",
        telephone: "",
        tel_professionnel: "",
        tel_portable: "",
        rue: "",
        numero: "",
        npa: undefined,
        ville: "",
        correspondance_email: true,
        etat_civil: "celibataire" as const,
        is_guarantor: preservedIsGuarantor,
        tenant_index: preservedTenantIndex,
      };

      // Remove from enterprise, add to individual
      const newEnterprise = enterpriseTenants.filter((_, i) => i !== arrayIndex);
      const newIndividual = [...individualTenants, newTenant];
      setValue("enterprise_tenants", newEnterprise);
      setValue("individual_tenants", newIndividual);
    } else {
      const newTenant = {
        status: "entreprise" as const,
        civilite: undefined as unknown as "monsieur" | "madame" | "ne_souhaite_pas_communiquer",
        nom_societe: "",
        tel_professionnel: "",
        tel_portable: "",
        nom_representant: "",
        prenom_representant: "",
        email_representant: "",
        rue: "",
        numero: "",
        npa: undefined,
        ville: "",
        correspondance_email: true,
        is_guarantor: preservedIsGuarantor,
        tenant_index: preservedTenantIndex,
      };

      // Remove from individual, add to enterprise
      const newIndividual = individualTenants.filter((_, i) => i !== arrayIndex);
      const newEnterprise = [...enterpriseTenants, newTenant];
      setValue("individual_tenants", newIndividual);
      setValue("enterprise_tenants", newEnterprise);
    }

    setShowConfirmDialog(false);
    setPendingType(null);
  };

  return (
    <>
      <div data-testid="type-switcher" className="flex flex-col gap-4 sm:gap-6 md:gap-[24px]">
        <p className="font-bold text-off-black text-base sm:text-lg md:text-[20px]">
          {t("form.selectStatus")}
        </p>
        <div className="bg-[#f6f6f8] rounded-[16px] p-2 sm:p-[10px] flex flex-col sm:flex-row gap-2 sm:gap-[20px]">
          <label className="flex-1 cursor-pointer">
            <input
              type="radio"
              name={`tenant-type-${displayIndex}`}
              value="particulier"
              checked={currentStatus === "particulier"}
              onChange={() => !disabled && handleTypeChange("particulier")}
              disabled={disabled}
              className="sr-only"
            />
            <div
              className={`rounded-[16px] px-4 sm:px-6 md:px-[48px] py-3 sm:py-4 md:py-[16px] flex items-center justify-center transition-colors ${
                currentStatus === "particulier" ? "bg-white text-[#e30615]" : "text-[#757288]"
              }`}
              onClick={() => !disabled && handleTypeChange("particulier")}
            >
              <span className="font-normal text-sm sm:text-base md:text-[20px] leading-[1.1] text-center">
                {t("form.status.particulier")}
              </span>
            </div>
          </label>
          <label className="flex-1 cursor-pointer">
            <input
              type="radio"
              name={`tenant-type-${displayIndex}`}
              value="entreprise"
              checked={currentStatus === "entreprise"}
              onChange={() => !disabled && handleTypeChange("entreprise")}
              disabled={disabled}
              className="sr-only"
            />
            <div
              className={`rounded-[16px] px-4 sm:px-6 md:px-[48px] py-3 sm:py-4 md:py-[16px] flex items-center justify-center transition-colors ${
                currentStatus === "entreprise" ? "bg-white text-[#e30615]" : "text-[#757288]"
              }`}
              onClick={() => !disabled && handleTypeChange("entreprise")}
            >
              <span className="font-normal text-sm sm:text-base md:text-[20px] leading-[1.1] text-center">
                {t("form.status.entreprise")}
              </span>
            </div>
          </label>
        </div>
      </div>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("form.warnings.typeSwitchTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("form.warnings.typeSwitchConfirmation")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowConfirmDialog(false);
                setPendingType(null);
              }}
            >
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingType) {
                  performTypeChange(pendingType);
                }
              }}
              className="bg-[#e30615] hover:bg-[#e30615]/90"
            >
              {t("common.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
